import React, { useEffect, useMemo, useState } from "react";
import { GlobalModal } from "../../../global/GlobalModal";
import { GlobalButton } from "../../../global/forms/GlobalFormFields";
import UserDirectoryMemberCell from "./UserDirectoryMemberCell";
import { formatShiftOptionLabel, formatTimeShort } from "./userBuilderHelpers";

function IconClose(props) {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function IconClock(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function IconPin(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M12 21s6-5.2 6-10a6 6 0 1 0-12 0c0 4.8 6 10 6 10z" />
      <circle cx="12" cy="11" r="2" />
    </svg>
  );
}

function IconSpark(props) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden {...props}>
      <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z" />
    </svg>
  );
}

function IconCheck(props) {
  return (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden {...props}>
      <path d="M20 6L9 17l-5-5" />
    </svg>
  );
}

function PickCard({ selected, auto, icon, title, subtitle, meta, onClick }) {
  return (
    <button
      type="button"
      className={`uaa-pick${selected ? " uaa-pick--selected" : ""}${auto ? " uaa-pick--auto" : ""}`}
      onClick={onClick}
      aria-pressed={selected}
    >
      <span className="uaa-pick__icon">{icon}</span>
      <span className="uaa-pick__body">
        <p className="uaa-pick__title">{title}</p>
        {subtitle ? <p className="uaa-pick__sub">{subtitle}</p> : null}
        {meta ? <span className="uaa-pick__meta">{meta}</span> : null}
      </span>
      <span className="uaa-pick__check" aria-hidden>
        {selected ? <IconCheck /> : null}
      </span>
    </button>
  );
}

function PreviewChip({ icon, label, value }) {
  return (
    <div className="uaa-preview-chip">
      <span className="uaa-preview-chip__icon">{icon}</span>
      <div className="uaa-preview-chip__body">
        <span className="uaa-preview-chip__label">{label}</span>
        <span className="uaa-preview-chip__value">{value}</span>
      </div>
    </div>
  );
}

export default function UserAttendanceAssignModal({
  open,
  user,
  shifts,
  locations,
  busy,
  onClose,
  onSave,
}) {
  const [shiftId, setShiftId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [activeTab, setActiveTab] = useState("shift");

  useEffect(() => {
    if (!open || !user) return;
    setShiftId(
      user.attendance_shift_id != null ? String(user.attendance_shift_id) : "",
    );
    setLocationId(
      user.attendance_location_id != null
        ? String(user.attendance_location_id)
        : "",
    );
    setActiveTab("shift");
  }, [open, user]);

  const selectedShift = useMemo(
    () => (shifts || []).find((s) => String(s.id) === String(shiftId)),
    [shifts, shiftId],
  );

  const selectedLocation = useMemo(
    () => (locations || []).find((loc) => String(loc.id) === String(locationId)),
    [locations, locationId],
  );

  const shiftSummary = shiftId && selectedShift
    ? formatShiftOptionLabel(selectedShift)
    : "Auto · by punch time";

  const locationSummary = locationId && selectedLocation
    ? selectedLocation.location_label ?? `Location #${selectedLocation.id}`
    : "Auto · nearest premises";

  const shiftPicker = (
    <div className="uaa-card-list">
      <PickCard
        auto
        selected={!shiftId}
        icon={<IconSpark />}
        title="Smart shift"
        subtitle="Late rules follow the shift window that matches punch time."
        meta="Recommended when hours vary"
        onClick={() => setShiftId("")}
      />
      {(shifts || []).length === 0 ? (
        <p className="uaa-empty">
          No shifts configured yet. Add them under Attendance control.
        </p>
      ) : (
        (shifts || []).map((s) => {
          const start = formatTimeShort(s.start_time);
          const end = formatTimeShort(s.end_time);
          const grace = s.grace_minutes != null ? `${s.grace_minutes}m grace` : null;
          return (
            <PickCard
              key={s.id}
              selected={String(shiftId) === String(s.id)}
              icon={<IconClock />}
              title={s.shift_name ?? "Shift"}
              subtitle={start && end ? `${start} – ${end}` : "Fixed timing"}
              meta={grace}
              onClick={() => setShiftId(String(s.id))}
            />
          );
        })
      )}
    </div>
  );

  const locationPicker = (
    <div className="uaa-card-list">
      <PickCard
        auto
        selected={!locationId}
        icon={<IconSpark />}
        title="Flexible premises"
        subtitle="User can punch at any allowed location within geofence."
        meta="Nearest match wins"
        onClick={() => setLocationId("")}
      />
      {(locations || []).length === 0 ? (
        <p className="uaa-empty">
          No locations configured yet. Add premises under Attendance control.
        </p>
      ) : (
        (locations || []).map((loc) => (
          <PickCard
            key={loc.id}
            selected={String(locationId) === String(loc.id)}
            icon={<IconPin />}
            title={loc.location_label ?? `Location #${loc.id}`}
            subtitle="Assigned user must punch inside this geofence."
            meta={
              loc.radius_meters != null
                ? `${loc.radius_meters}m radius`
                : "Geo-fenced"
            }
            onClick={() => setLocationId(String(loc.id))}
          />
        ))
      )}
    </div>
  );

  return (
    <GlobalModal
      open={open}
      onClose={() => !busy && onClose()}
      showCloseButton={false}
      size="lg"
      classNames={{
        modal: "gff-modal gff-modal--lg uaa-modal",
        body: "gff-modal__body uaa-modal__body",
        footer: "gff-modal__footer uaa-modal__footer",
      }}
      footer={
        <div className="uaa-footer">
          <div className="uaa-footer__summary" aria-live="polite">
            <span className="uaa-footer__pill">
              <IconClock width={12} height={12} aria-hidden />
              <span>{shiftSummary}</span>
            </span>
            <span className="uaa-footer__pill">
              <IconPin width={12} height={12} aria-hidden />
              <span>{locationSummary}</span>
            </span>
          </div>
          <div className="uaa-footer__actions">
            <GlobalButton type="button" variant="ghost" onClick={onClose} disabled={busy}>
              Cancel
            </GlobalButton>
            <GlobalButton
              type="button"
              variant="primary"
              className="uaa-save-btn"
              onClick={() =>
                onSave({
                  attendance_shift_id: shiftId ? Number(shiftId) : null,
                  attendance_location_id: locationId ? Number(locationId) : null,
                })
              }
              loading={busy}
            >
              Save assignment
            </GlobalButton>
          </div>
        </div>
      }
    >
      <div className="uaa-shell" role="document" aria-labelledby="uaa-modal-title">
        <aside className="uaa-aside">
          <button
            type="button"
            className="uaa-aside__close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close dialog"
          >
            <IconClose />
          </button>

          <div className="uaa-aside__profile">
            <UserDirectoryMemberCell
              photo={user?.photo}
              name={user?.name}
              userCode={user?.user_code}
              layout="profile"
            />
          </div>

          <div className="uaa-aside__heading">
            <p className="uaa-aside__eyebrow">Attendance profile</p>
            <h2 id="uaa-modal-title" className="uaa-aside__title">
              Shift &amp; location
            </h2>
          </div>

          <div className="uaa-aside__preview" aria-label="Selected assignment">
            <p className="uaa-aside__preview-label">Selected</p>
            <PreviewChip
              icon={<IconClock width={15} height={15} aria-hidden />}
              label="Shift"
              value={shiftSummary}
            />
            <PreviewChip
              icon={<IconPin width={15} height={15} aria-hidden />}
              label="Location"
              value={locationSummary}
            />
          </div>
        </aside>

        <main className="uaa-main">
          <div className="uaa-tabs" role="tablist" aria-label="Assignment options">
            <button
              type="button"
              role="tab"
              id="uaa-tab-shift"
              aria-selected={activeTab === "shift"}
              aria-controls="uaa-panel-shift"
              className={`uaa-tab${activeTab === "shift" ? " uaa-tab--active" : ""}`}
              onClick={() => setActiveTab("shift")}
            >
              <IconClock width={15} height={15} aria-hidden />
              Shift
            </button>
            <button
              type="button"
              role="tab"
              id="uaa-tab-location"
              aria-selected={activeTab === "location"}
              aria-controls="uaa-panel-location"
              className={`uaa-tab${activeTab === "location" ? " uaa-tab--active" : ""}`}
              onClick={() => setActiveTab("location")}
            >
              <IconPin width={15} height={15} aria-hidden />
              Location
            </button>
          </div>

          <div className="uaa-scroll">
            <p className="uaa-section-title">
              {activeTab === "shift" ? "Choose shift timing" : "Choose attendance premises"}
            </p>
            {activeTab === "shift" ? (
              <section id="uaa-panel-shift" role="tabpanel" aria-labelledby="uaa-tab-shift">
                {shiftPicker}
              </section>
            ) : (
              <section id="uaa-panel-location" role="tabpanel" aria-labelledby="uaa-tab-location">
                {locationPicker}
              </section>
            )}
          </div>
        </main>
      </div>
    </GlobalModal>
  );
}
